import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../api';

const PAGE_NAMES = {
  '/dashboard':    'Dashboard',
  '/jobs':         'Browse Jobs',
  '/profile':      'Profile',
  '/applications': 'Applications',
  '/admin':        'Admin',
};

const TYPES = ['Bug', 'Suggestion', 'Positive', 'Other'];

const TYPE_COLORS = {
  Bug:        { bg: 'var(--tandm-danger-bg)',  color: 'var(--tandm-danger-fg)',  border: 'var(--tandm-danger-border)',  active: 'var(--tandm-danger-fg)' },
  Suggestion: { bg: 'var(--tandm-info-bg)',    color: 'var(--tandm-info-fg)',    border: 'var(--tandm-info-border)',    active: 'var(--tandm-info-fg)' },
  Positive:   { bg: 'var(--tandm-success-bg)', color: 'var(--tandm-success-fg)', border: 'var(--tandm-success-border)', active: 'var(--tandm-success-fg)' },
  Other:      { bg: 'var(--tandm-canvas-deep)',color: 'var(--tandm-body)',       border: 'var(--tandm-line)',           active: 'var(--tandm-accent-deep)' },
};

const Icons = {
  message: () => (
    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
    </svg>
  ),
  close: () => (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
    </svg>
  ),
  send: () => (
    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
    </svg>
  ),
  check: () => (
    <svg width="20" height="20" fill="none" stroke="var(--tandm-success-fg)" viewBox="0 0 24 24" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
    </svg>
  ),
};

export default function FeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Don't show on landing page
  const hidden = ['/', '/login', '/signup'].includes(location.pathname);
  if (hidden) return null;

  const pageName = PAGE_NAMES[location.pathname]
    || (location.pathname.startsWith('/jobs/') ? 'Job Detail' : 'Page');

  const handleOpen = () => {
    setOpen(true);
    setDone(false);
    setMessage('');
    setType('');
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setMessage(''); setType(''); }, 300);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/feedback', { page: pageName, type: type || 'General', message });
      setDone(true);
      setTimeout(() => { handleClose(); }, 2200);
    } catch {
      // still close gracefully
      setDone(true);
      setTimeout(() => { handleClose(); }, 2200);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .fw-textarea { resize: none; font-family: var(--tandm-font-sans); }
        .fw-textarea:focus { outline: none; border-color: var(--tandm-accent-hot) !important; box-shadow: 0 0 0 3px rgba(108,150,156,0.12); }
        .fw-textarea::placeholder { color: var(--tandm-faint); }
      `}</style>

      {/* Floating button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            whileHover={{ scale: 1.06, boxShadow: '0 8px 28px rgba(78,108,126,0.4)' }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOpen}
            style={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, var(--tandm-surface-dark), var(--tandm-surface-dark-elevated))',
              color: '#fff', border: '1px solid rgba(124,172,183,0.3)',
              borderRadius: 100, padding: '11px 18px',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'var(--tandm-font-sans)',
              boxShadow: '0 4px 20px rgba(11,18,32,0.25)',
            }}
          >
            <span style={{ color: 'var(--tandm-accent-hot)' }}><Icons.message /></span>
            Feedback
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              style={{ position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(11,18,32,0.18)', backdropFilter: 'blur(2px)' }}
            />

            {/* Card */}
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              style={{
                position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
                width: 340,
                background: 'var(--tandm-surface)',
                border: '1px solid var(--tandm-line)',
                borderRadius: 22,
                boxShadow: 'var(--tandm-shadow-lift)',
                overflow: 'hidden',
                fontFamily: 'var(--tandm-font-sans)',
              }}
            >
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, var(--tandm-surface-dark), var(--tandm-surface-dark-elevated))',
                padding: '16px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, var(--tandm-accent-hot) 0%, transparent 70%)', right: -20, top: -40, opacity: 0.28, pointerEvents: 'none' }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>Share feedback</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>Page:</span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tandm-accent-light)', background: 'rgba(124,172,183,0.15)', border: '1px solid rgba(124,172,183,0.28)', padding: '1px 8px', borderRadius: 100 }}>{pageName}</span>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, background: 'rgba(255,255,255,0.15)' }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleClose}
                  style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.6)', position: 'relative', zIndex: 1 }}
                >
                  <Icons.close />
                </motion.button>
              </div>

              <AnimatePresence mode="wait">
                {done ? (
                  <motion.div
                    key="done"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10 }}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                      style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--tandm-success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}
                    >
                      <Icons.check />
                    </motion.div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--tandm-ink)', margin: 0, letterSpacing: '-0.02em' }}>Thanks for the feedback!</p>
                    <p style={{ fontSize: 12, color: 'var(--tandm-muted)', margin: 0, lineHeight: 1.55 }}>Every piece helps us improve Tandm for you.</p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ padding: '18px 20px 20px' }}
                  >
                    {/* Type pills */}
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--tandm-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 10px' }}>Type</p>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 16 }}>
                      {TYPES.map(t => {
                        const cfg = TYPE_COLORS[t];
                        const active = type === t;
                        return (
                          <motion.button
                            key={t}
                            whileHover={{ y: -1 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setType(active ? '' : t)}
                            style={{
                              fontSize: 12, fontWeight: 600, padding: '5px 13px', borderRadius: 100,
                              cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
                              border: active ? 'none' : `1.5px solid ${cfg.border}`,
                              background: active ? cfg.active : cfg.bg,
                              color: active ? '#fff' : cfg.color,
                              transition: 'all 0.15s',
                            }}
                          >
                            {t}
                          </motion.button>
                        );
                      })}
                    </div>

                    {/* Message */}
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--tandm-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px' }}>Message</p>
                    <textarea
                      className="fw-textarea"
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      placeholder="Tell us what you think, what's broken, or what you'd love to see..."
                      rows={4}
                      style={{
                        width: '100%', padding: '11px 13px',
                        border: '1.5px solid var(--tandm-line)', borderRadius: 12,
                        fontSize: 13, color: 'var(--tandm-ink)', background: 'var(--tandm-canvas)',
                        lineHeight: 1.55, transition: 'all 0.2s',
                      }}
                    />

                    {/* User info */}
                    {user && (
                      <p style={{ fontSize: 11, color: 'var(--tandm-faint)', margin: '8px 0 14px', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                        Sending as {user.first_name} {user.last_name} · {user.email}
                      </p>
                    )}

                    {/* Submit */}
                    <motion.button
                      whileHover={{ scale: 1.02, boxShadow: '0 6px 20px rgba(78,108,126,0.35)' }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleSubmit}
                      disabled={!message.trim() || submitting}
                      style={{
                        width: '100%', padding: '12px',
                        background: !message.trim() ? 'var(--tandm-canvas-deep)' : 'var(--tandm-gradient-cta)',
                        color: !message.trim() ? 'var(--tandm-faint)' : 'var(--tandm-on-accent)',
                        border: 'none', borderRadius: 12,
                        fontSize: 14, fontWeight: 600, cursor: !message.trim() ? 'not-allowed' : 'pointer',
                        fontFamily: 'var(--tandm-font-sans)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        transition: 'all 0.2s',
                      }}
                    >
                      {submitting
                        ? <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                        : <><Icons.send /> Send feedback</>
                      }
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
